import * as React from 'react';
import axios from 'axios';
import { Alert, Button, Checkbox, Modal, notification, Radio, Select, Table, Upload } from 'antd';
import { DownloadOutlined, FileExcelOutlined, InboxOutlined } from '@ant-design/icons';

import { url } from '../../config/config';

type TReplace = 'price' | 'recommended' | 'both';

interface IPriceChange {
  currencyId: string;
  oldPrice: number | null;
  newPrice: number | null;
  oldRecommendedPrice: number | null;
  newRecommendedPrice: number | null;
}

interface IPreview {
  hasHeader: boolean;
  skipFirstRow: boolean;
  columns: Array<{ index: number; header: string | null; detectedCurrencyId: string | null; currencyId: string | null }>;
  currencies: Array<{ id: string; value: string }>;
  items: Array<{
    row: number;
    sku: string;
    ean: string;
    variantId: string;
    productName: string;
    changed: boolean;
    prices: IPriceChange[];
  }>;
  unmatched: Array<{ row: number; sku: string }>;
  invalid: Array<{ row: number; sku: string }>;
}

interface IProductPriceImportProps {
  onClose: () => void;
  onImported: () => void;
}

interface IProductPriceImportState {
  file?: File;
  preview?: IPreview;
  skipFirstRow?: boolean;
  columnCurrencies?: Array<string | null>;
  replace: TReplace;
  loading: boolean;
  applying: boolean;
  changesOnly: boolean;
}

const colors = {
  muted: '#8c8c8c',
  up: { background: '#f6ffed', color: '#389e0d', border: '1px solid #d9f7be' },
  down: { background: '#fff1f0', color: '#cf1322', border: '1px solid #ffccc7' },
  same: { background: '#fafafa', color: '#bfbfbf', border: '1px solid #f0f0f0' },
};

const formatPrice = (value: number | null) =>
  value === null || value === undefined
    ? '—'
    : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PriceDiff = ({ label, oldValue, newValue }: { label?: string; oldValue: number | null; newValue: number | null }) => {
  const percent = oldValue && newValue !== null ? ((newValue - oldValue) / oldValue) * 100 : null;
  const pill = percent === null || Math.abs(percent) < 0.05 ? colors.same : percent > 0 ? colors.up : colors.down;

  return (
    <div style={{ whiteSpace: 'nowrap', lineHeight: '18px' }}>
      <div>
        {label && <span style={{ color: colors.muted, fontSize: 11, marginRight: 4 }}>{label}</span>}
        <span style={{ color: colors.muted }}>{formatPrice(oldValue)}</span>
        <span style={{ color: colors.muted, margin: '0 4px' }}>→</span>
        <strong>{formatPrice(newValue)}</strong>
      </div>
      <span style={{ ...pill, display: 'inline-block', fontSize: 11, lineHeight: '16px', padding: '0 6px', borderRadius: 8 }}>
        {percent === null ? 'new' : Math.abs(percent) < 0.05 ? 'no change' : `${percent > 0 ? '+' : ''}${percent.toFixed(1)} %`}
      </span>
    </div>
  );
};

export default class ProductPriceImport extends React.Component<IProductPriceImportProps, IProductPriceImportState> {

  constructor(props: IProductPriceImportProps) {
    super(props);

    this.state = {
      replace: 'price',
      loading: false,
      applying: false,
      changesOnly: false,
    };
  }

  public render() {
    const { file, preview, loading, applying } = this.state;
    const changedCount = preview ? preview.items.filter(i => i.changed).length : 0;

    return (
      <Modal
        visible={true}
        width="100vw"
        style={{ top: 0, maxWidth: '100vw', margin: 0, paddingBottom: 0 }}
        title="Import product prices"
        bodyStyle={{ padding: '12px 24px' }}
        onCancel={() => this.props.onClose()}
        footer={
          <div style={{ display: 'flex' }}>
            <Button icon={<DownloadOutlined />} disabled={!preview || preview.items.length === 0 || loading} onClick={() => this.downloadBackup()}>
              Download backup
            </Button>
            <span style={{ marginLeft: 'auto' }}>
              <Button onClick={() => this.props.onClose()}>Cancel</Button>
              <Button type="primary" disabled={changedCount === 0 || loading} loading={applying} onClick={() => this.confirm()}>
                {changedCount > 0 ? `Import ${changedCount} prices` : 'Import'}
              </Button>
            </span>
          </div>
        }
      >
        {file ? this.renderToolbar(file, preview) : (
          <Upload.Dragger {...this.uploadProps()}>
            <div style={{ padding: '4px 0' }}>
              <InboxOutlined style={{ fontSize: 28, color: '#1890ff' }} />
              <div>Click or drag an .xlsx or .csv file here</div>
              <div style={{ color: colors.muted, fontSize: 12 }}>First column = SKU, next columns = prices</div>
            </div>
          </Upload.Dragger>
        )}
        {preview && this.renderMessages(preview)}
        {(preview || loading) && this.renderItems(preview)}
      </Modal>
    );
  }

  private uploadProps() {
    return {
      accept: '.xlsx,.xls,.csv',
      multiple: false,
      showUploadList: false,
      beforeUpload: (file: File) => {
        this.setState(
          { file, preview: undefined, skipFirstRow: undefined, columnCurrencies: undefined },
          () => this.requestPreview(),
        );
        return false;
      },
    };
  }

  private renderToolbar(file: File, preview?: IPreview) {
    const changedCount = preview ? preview.items.filter(i => i.changed).length : 0;

    return (
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginBottom: 8 }}>
        <Upload {...this.uploadProps()}>
          <Button size="small" icon={<FileExcelOutlined />} title="Choose another file">{file.name}</Button>
        </Upload>
        <Checkbox
          checked={this.state.skipFirstRow}
          disabled={!preview}
          onChange={e => this.changeOptions({ skipFirstRow: e.target.checked })}
        >
          Ignore first row
          {preview && preview.hasHeader && <span style={{ color: colors.muted, fontSize: 12 }}> (header detected)</span>}
        </Checkbox>
        <Radio.Group
          size="small"
          buttonStyle="solid"
          value={this.state.replace}
          disabled={!preview}
          onChange={e => this.changeOptions({ replace: e.target.value })}
        >
          <Radio.Button value="price">Price</Radio.Button>
          <Radio.Button value="recommended">Recommended</Radio.Button>
          <Radio.Button value="both">Both</Radio.Button>
        </Radio.Group>
        <Checkbox
          checked={this.state.changesOnly}
          disabled={!preview}
          onChange={e => this.setState({ changesOnly: e.target.checked })}
        >
          Show changes only
        </Checkbox>
        {preview &&
          <span style={{ marginLeft: 'auto', color: colors.muted, fontSize: 12 }}>
            {preview.items.length} matched · <strong style={{ color: 'inherit' }}>{changedCount} changed</strong>
            {preview.unmatched.length > 0 && ` · ${preview.unmatched.length} not found`}
          </span>}
      </div>
    );
  }

  private renderMessages(preview: IPreview) {
    const list = (rows: Array<{ row: number; sku: string }>) => rows.map(u => `${u.sku} (row ${u.row})`).join(', ');
    const style = { marginBottom: 8, padding: '2px 12px', fontSize: 12 };

    return (
      <React.Fragment>
        {!(this.state.columnCurrencies || []).some(id => !!id) &&
          <Alert type="info" showIcon={true} style={style} message="Choose a currency in the header of at least one price column." />}
        {preview.unmatched.length > 0 &&
          <Alert type="warning" showIcon={true} style={style} message={`SKUs not found: ${list(preview.unmatched)}`} />}
        {preview.invalid.length > 0 &&
          <Alert type="warning" showIcon={true} style={style} message={`Rows without a valid price: ${list(preview.invalid)}`} />}
      </React.Fragment>
    );
  }

  private renderColumnTitle(preview: IPreview, column: IPreview['columns'][0], i: number) {
    const columnCurrencies = this.state.columnCurrencies || [];

    return (
      <div style={{ textAlign: 'right' }}>
        <Select
          size="small"
          style={{ width: 120 }}
          value={columnCurrencies[i] || ''}
          onChange={(value: string) => {
            const next = [...columnCurrencies];
            next[i] = value || null;
            this.changeOptions({ columnCurrencies: next });
          }}
        >
          <Select.Option value="">Skip column</Select.Option>
          {preview.currencies.map(c => (
            <Select.Option key={c.id} value={c.id} disabled={columnCurrencies.some((id, j) => id === c.id && j !== i)}>
              {c.value}
            </Select.Option>
          ))}
        </Select>
        <div style={{ color: colors.muted, fontSize: 11, fontWeight: 'normal', marginTop: 2 }}>
          {column.header || `Column ${column.index + 1}`}
        </div>
      </div>
    );
  }

  private renderItems(preview?: IPreview) {
    const { replace } = this.state;
    const columnCurrencies = this.state.columnCurrencies || [];

    return (
      <Table
        size="small"
        loading={this.state.loading}
        rowKey="variantId"
        dataSource={preview ? preview.items.filter(i => i.changed || !this.state.changesOnly) : []}
        pagination={false}
        scroll={{ y: 'calc(100vh - 260px)' }}
        onRow={record => ({ style: record.changed ? {} : { opacity: 0.45 } })}
      >
        <Table.Column
          title="Product"
          key="product"
          ellipsis={true}
          render={(u, record: IPreview['items'][0]) => (
            <div style={{ lineHeight: '18px' }}>
              <strong
                title={record.productName}
                style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {record.productName}
              </strong>
              <div style={{ color: colors.muted, fontSize: 12 }}>
                {record.sku}<span style={{ fontSize: 11, marginLeft: 8 }}>row {record.row}</span>
              </div>
            </div>
          )}
        />
        {preview && preview.columns.map((column, i) => (
          <Table.Column
            key={column.index}
            title={this.renderColumnTitle(preview, column, i)}
            align="right"
            width={replace === 'both' ? 190 : 170}
            render={(u, record: IPreview['items'][0]) => {
              const change = columnCurrencies[i] && record.prices.find(p => p.currencyId === columnCurrencies[i]);
              if (!change) {
                return <span style={{ color: colors.muted }}>—</span>;
              }

              return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  {replace !== 'recommended' &&
                    <PriceDiff label={replace === 'both' ? 'price' : undefined} oldValue={change.oldPrice} newValue={change.newPrice} />}
                  {replace !== 'price' &&
                    <PriceDiff
                      label={replace === 'both' ? 'rec.' : undefined}
                      oldValue={change.oldRecommendedPrice}
                      newValue={change.newRecommendedPrice}
                    />}
                </div>
              );
            }}
          />
        ))}
      </Table>
    );
  }

  private changeOptions(options: Partial<IProductPriceImportState>) {
    this.setState(options as IProductPriceImportState, () => this.requestPreview());
  }

  private getOptions() {
    return {
      skipFirstRow: this.state.skipFirstRow,
      columnCurrencies: this.state.columnCurrencies,
      replace: this.state.replace,
    };
  }

  private send(action: 'preview' | 'apply') {
    const data = new FormData();
    data.append('file', this.state.file as File);
    data.append('options', JSON.stringify(this.getOptions()));

    return axios.post(`${url}/products/importPrices/${action}`, data, {
      headers: { Authorization: localStorage.getItem('token') },
    });
  }

  private requestPreview() {
    if (!this.state.file) {
      return;
    }

    this.setState({ loading: true });
    this.send('preview')
      .then(response => {
        const preview: IPreview = response.data;
        this.setState({
          preview,
          loading: false,
          skipFirstRow: preview.skipFirstRow,
          columnCurrencies: preview.columns.map(c => c.currencyId),
        });
      })
      .catch(error => {
        this.setState({ loading: false, preview: undefined });
        this.showError(error);
      });
  }

  // current prices of the imported currencies in the import format, so they can be imported back
  private downloadBackup() {
    const { preview, replace } = this.state;
    if (!preview) {
      return;
    }

    const fields: Array<{ key: 'oldPrice' | 'oldRecommendedPrice'; suffix: string }> = [];
    if (replace !== 'recommended') {
      fields.push({ key: 'oldPrice', suffix: '' });
    }
    if (replace !== 'price') {
      fields.push({ key: 'oldRecommendedPrice', suffix: ' recommended' });
    }

    const currencyIds = (this.state.columnCurrencies || []).filter((id): id is string => !!id);
    const columns = currencyIds.reduce((acc: Array<{ currencyId: string; key: 'oldPrice' | 'oldRecommendedPrice'; title: string }>, currencyId) => {
      const currency = preview.currencies.find(c => c.id === currencyId);
      fields.forEach(f => acc.push({ currencyId, key: f.key, title: `${currency ? currency.value : currencyId}${f.suffix}` }));
      return acc;
    }, []);

    const skus: string[] = [];
    const lines = [['SKU', ...columns.map(c => c.title)].join(';')];
    for (const item of preview.items) {
      if (skus.indexOf(item.sku) >= 0) {
        continue;
      }
      skus.push(item.sku);
      lines.push([item.sku, ...columns.map(c => {
        const change = item.prices.find(p => p.currencyId === c.currencyId);
        const value = change ? change[c.key] : null;
        return value === null || value === undefined ? '' : String(value);
      })].join(';'));
    }

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' }));
    link.download = `prices-backup-${new Date().toISOString().slice(0, 16).replace(/[T:]/g, '-')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private confirm() {
    const { preview } = this.state;
    if (!preview) {
      return;
    }

    const changed = preview.items.filter(i => i.changed);
    const values = (p: IPriceChange) => this.state.replace === 'recommended'
      ? [p.oldRecommendedPrice, p.newRecommendedPrice]
      : [p.oldPrice, p.newPrice];
    const direction = (sign: number) => changed.filter(i => i.prices.some(p => {
      const [oldValue, newValue] = values(p);
      return oldValue !== null && newValue !== null && Math.sign(newValue - oldValue) === sign;
    })).length;

    Modal.confirm({
      title: 'Do you really want to import prices?',
      content: (
        <span>
          Prices of <strong>{changed.length}</strong> product variants will be overwritten
          ({direction(1)} increased, {direction(-1)} decreased). This cannot be undone.
        </span>
      ),
      okText: 'Yes, import prices',
      okButtonProps: { danger: true },
      onOk: () => this.apply(),
    });
  }

  private apply() {
    this.setState({ applying: true });

    return this.send('apply')
      .then(response => {
        this.setState({ applying: false });
        notification.success({
          message: 'Prices imported',
          description: `Prices of ${response.data.updated} product variants have been updated.`,
        });
        this.props.onImported();
      })
      .catch(error => {
        this.setState({ applying: false });
        this.showError(error);
      });
  }

  private showError(error: any) {
    notification.error({
      message: 'Price import failed',
      description: (error.response && error.response.data && error.response.data.message) || error.message,
    });
  }
}



// WEBPACK FOOTER //
// ./src/scenes/Products/ProductPriceImport.tsx